import ApplicationResponsiveModal from '../common/ApplicationResponsiveModal'

export const TermsModal = ({show, onClose}: {show: boolean; onClose: () => void}) => {
  if (!show) return null

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='application-terms-title'
      closeAriaLabel='Close terms of use'
      onClose={onClose}
      modalClassName='application-legal-modal'
      closeButtonClassName='application-legal-modal-close'
      hideCloseButtonOnMobile
      mobileAnimationMs={380}
      swipeTarget='handle'
    >
      <div className='application-legal-modal-body'>
        <div className='application-legal-modal-content terms'>
          <h2 id='application-terms-title' className='title'>
            DOORA
          </h2>
          <h3 className='subtitle'>Renter Terms Of Service</h3>
          <hr></hr>
          <p className='caption'>
            Operated by RL Group Australia Pty Ltd (ACN 690 919 307)
            <br />
            trading as DOORA
            <br />
            <strong>Effective Date: 1 July 2026</strong>
          </p>
        <h4>What is this document?</h4>
        <p>
          By creating a DOORA account, ticking the acceptance box at signup, or using the service,
          you accept these Terms and the DOORA Privacy Policy. If you do not accept them, please do
          not use DOORA.
        </p>
        <p>
          This document explains the rules for using DOORA. It is written in plain English for
          clarity. In summary: DOORA is free for renters, we do not sell your data, and you are
          always in control of what happens to your Doora Card.
        </p>
        <p>
          Your use of the platform is also governed by the DOORA Privacy Policy, which is a separate
          document available at doora.com.au/privacy. If there is any inconsistency between this
          document and the Privacy Policy on a privacy-related matter, the Privacy Policy prevails.
        </p>
        <h3>PART A — Terms of Service</h3>
        <p>These terms govern your use of the DOORA platform and Helper service.</p>
        <h4>A0 Eligibility</h4>
        <p>
          You must be at least 18 years old to create a DOORA account or build a Doora Card. By
          using the platform, you confirm that you are 18 or older and that you are legally capable
          of entering into a binding agreement. If you are under 18, you may not use DOORA. This
          requirement exists because residential tenancy agreements in Australia can only be entered
          into by adults, and DOORA collects sensitive personal information (including government
          identification and financial records) that minors should not be submitting without
          independent legal capacity. If DOORA discovers that a user is under 18, the account will
          be suspended and the associated personal information deleted within 7 days, in accordance
          with the Privacy Act 1988 (Cth).
        </p>
        <h4>A1 What DOORA Is</h4>
        <p>
          DOORA helps renters prepare for the rental market. We give you the tools, guidance, and a
          structured application document — your Doora Card — so you can present a complete
          application when applying for a rental property.
        </p>
        <p>
          Everything DOORA provides to renters is free. The DOORA Card, the Step 0 guidance, the
          Helper support, and all platform features are free for you to use. DOORA does not
          currently charge renters for any service and will provide at least 30 days’ notice before
          any pricing change.
        </p>
        <p>
          DOORA is a renter preparation service. We are not a real estate agent, a property manager,
          a listing platform, or a matching service. We do not control or influence rental outcomes.
          We prepare you — the rest is up to the market.
        </p>
        <h5>What DOORA is NOT</h5>
        <ul>
          <li>We are not a licensed real estate agent or property manager.</li>
          <li>We do not provide legal advice, financial advice, or tenancy advocacy.</li>
          <li>We do not recommend specific properties, agents, or landlords.</li>
          <li>We do not guarantee you will get a rental property.</li>
          <li>
            We do not score, rank, or compare you against other renters. There is no premium tier.
          </li>
          <li>We are not a credit check or tenant screening service.</li>
        </ul>
        <p>
          If your situation requires legal help (a dispute with a landlord, a notice to vacate, an
          issue with a bond), we will refer you to the right free service in your state. We do not
          provide assistance outside our defined scope of service.
        </p>
        <h5>A1A Your Account and Security</h5>
        <p>
          You are responsible for keeping your DOORA login credentials and verification codes
          secure. Do not share your one-time passwords (OTPs) with anyone. If you believe someone
          has accessed your account without your permission, contact us immediately through the
          platform or by messaging a Helper. DOORA is not responsible for any actions taken on your
          account by someone who accessed it using your credentials, including submissions made to
          property managers or changes to your Doora Card. If you are part of a shared household
          Card, all verified household members have legitimate access to the shared dashboard.
        </p>
        <h4>A2 Your Doora Card</h4>
        <p>
          The Doora Card is a portable, standardised rental application document that you build
          through the DOORA platform. You enter your own information — on your own device — at your
          own pace. Our Helpers guide you through the process via chat, but they do not enter
          information on your behalf.
        </p>
        <h5>Your Card belongs to you</h5>
        <ul>
          <li>
            You own your Doora Card. We hold a copy to provide the service. We do not use it for
            anything else.
          </li>
          <li>
            You decide which property managers see your Card. You submit it to a specific property,
            at a specific time, by your own choice. Nothing is sent automatically.
          </li>
          <li>
            You can edit your Card at any time. Your edits do not change what a property manager has
            already received — what they saw at submission is locked as a snapshot.
          </li>
          <li>
            You can delete your Card and all your data at any time. Submit a request through the
            platform or message a Helper.
          </li>
        </ul>
        <h5>How retention works</h5>
        <p>When you create your Doora Card, you choose how long we keep it:</p>
        <ul>
          <li>Keep it active until you delete it yourself</li>
          <li>Auto-delete after 60 days</li>
          <li>Auto-delete after 90 days</li>
        </ul>
        <p>
          Regardless of your retention selection, you may request immediate deletion at any time. We
          will confirm when it is done.
        </p>
        <h4>A3 Submitting Your Card to a Property Manager</h4>
        <p>
          The only way a property manager receives your Doora Card is when you go to your dashboard
          and choose to submit it to a specific property. Nothing happens automatically. There is no
          other way.
        </p>
        <h5>Snapshot rule</h5>
        <p>
          Every time you submit your Card to a property, we create a frozen copy of your Card at
          that moment. The property manager sees exactly what you sent. If you update your Card
          after submitting, the property manager does not see those changes — your original
          submission stays as it was. If you want them to see updated information, submit again.
        </p>
        <h5>What property managers can do</h5>
        <ul>
          <li>View your Doora Card via a secure link we send them.</li>
          <li>Return to the link multiple times within 30 days.</li>
        </ul>
        <h5>What property managers cannot do</h5>
        <ul>
          <li>
            Reproduce, download, or share Doora Card content beyond what is necessary for assessing
            the tenancy application.
          </li>
          <li>Share your information with anyone else.</li>
          <li>
            Use your information for any purpose other than assessing your application for that
            property.
          </li>
        </ul>
        <p>
          Property managers agree to these rules before they can access any Doora Card. Their
          obligations are set out in a separate agreement they sign with DOORA.
        </p>
        <h5>Revoking access</h5>
        <p>
          You may revoke a property manager’s access to your Card at any time. The moment you
          revoke, their access link is invalidated. No notification is sent to the property manager.
          You get a confirmation on your dashboard.
        </p>
        <h4>A4 Household Applications (Co-Applicants)</h4>
        <p>
          If you are applying with housemates, partners, or family, you can build a shared Doora
          Card. Each person in the household joins by verifying their phone number via a code we
          send them.
        </p>
        <p>
          When we send that verification code to your co-applicant’s phone, the message will tell
          them: who shared their number with us (the primary applicant), what DOORA is, and where to
          find our Privacy Policy. This serves as their first notification under APP 5 of the
          Privacy Act 1988 (Cth).
        </p>
        <ul>
          <li>All household members see the same dashboard and submission history.</li>
          <li>
            The primary applicant — the person who first created the Card and invited the
            co-applicant(s) — holds primary decision-making authority over the Card as a whole,
            including sharing, revocation, and deletion.
          </li>
          <li>
            If the primary applicant revokes a property manager’s access, it is revoked for the
            entire household Card.
          </li>
          <li>
            Co-applicants can manage their own personal data within the Card (edit, correct, or
            request deletion of their own information) but cannot override the primary applicant’s
            decisions about the Card as a whole.
          </li>
          <li>
            Any household member can leave the shared Card at any time and request deletion of their
            own data.
          </li>
          <li>
            Any verified household member may submit the shared Doora Card to a property on behalf
            of the household. When a co-applicant submits, all household members are notified on the
            shared dashboard. The primary applicant may restrict submission rights at any time
            through the dashboard settings.
          </li>
          <li>
            You may only belong to one household Card at a time. If you want to join a different
            household, you must first leave your current one. This prevents the same person from
            appearing on multiple active applications simultaneously.
          </li>
          <li>
            Disputes between household members about the shared Card, submissions, or access
            decisions are between the members of that household. DOORA does not mediate, arbitrate,
            or intervene in disagreements between co-applicants. If household members cannot agree,
            any member may leave the shared Card and create their own individual Card at any time.
          </li>
        </ul>
        <p>
          If you have a concern about your own personal information within a shared Card, contact us
          directly. We handle individual requests separately through our support channel.
        </p>
        <h4>A5 Our Helpers</h4>
        <p>
          DOORA Helpers are trained staff who support you through the renter preparation process via
          WhatsApp, Instagram, or Facebook Messenger. Helpers help you build a stronger Doora Card
          and understand what you need to do to be a competitive applicant.
        </p>
        <h5>What Helpers can do for you</h5>
        <ul>
          <li>Guide you step by step through the Doora Card creation process.</li>
          <li>Explain what information is required and why. </li>
          <li>
            Provide general guidance on rental application standards, presentation, and what makes
            an application competitive.{' '}
          </li>
          <li>
            Refer you to an appropriate external service where your situation requires legal,
            financial, or specialist tenancy support.
          </li>
        </ul>
        <h5>What Helpers cannot do</h5>
        <ul>
          <li>
            Enter information into your Doora Card on your behalf. You always enter your own data on
            your own device.
          </li>
          <li>Give you legal advice, financial advice, or tenancy advocacy.</li>
          <li>Negotiate with property managers on your behalf.</li>
          <li>Guarantee any housing outcome.</li>
        </ul>
        <p>
          Nothing a Helper says is legal advice. If your situation involves a legal dispute, a
          notice to vacate, a discrimination issue, or any matter that needs a qualified
          professional, your Helper will refer you to the right free service.
        </p>
        <p>
          When you communicate with a Helper, your messages pass through our customer support
          ticketing platform and may be accessible to overseas Helper staff. For details of how we
          handle your information when it is processed overseas — including the contractual
          protections we have in place — see the DOORA Privacy Policy (Sections 9 and 10).
        </p>
        <h4>A6 Communications We Send You</h4>
        <p>
          We only contact you when it is directly related to your use of DOORA. We do not send
          marketing emails, newsletters, or promotional content.
        </p>
        <h5>The messages we send:</h5>
        <ul>
          <li>Login verification code (OTP) — when you log in or create your account.</li>
          <li>
            Co-applicant verification code (OTP) — when a housemate adds your number to a shared
            Card.
          </li>
          <li>
            Draft reminder — if you started building your Card but didn’t finish, we’ll send one
            text message reminder after a few days, and one email reminder if you still haven’t
            completed it. A maximum of two reminder messages will be sent. No further messages will
            follow.
          </li>
          <li>Property manager share notification — when your Card is sent to a PM.</li>
        </ul>
        <p>
          You can stop draft reminders at any time by completing your Card, deleting your draft, or
          asking a Helper to remove your contact details.
        </p>
        <h4>A7 Our Liability to You</h4>
        <p>
          DOORA provides the platform and Helper guidance free of charge. To the maximum extent
          permitted by the Australian Consumer Law and the Competition and Consumer Act 2010 (Cth):
        </p>
        <ul>
          <li>
            We are not responsible for any housing outcome — whether you are approved, rejected, or
            delayed. Rental decisions are made by property managers and landlords, not by DOORA.
          </li>
          <li>
            We are not responsible for the accuracy of the information you put in your Doora Card.
            You are responsible for making sure your information is true and complete.
          </li>
          <li>
            We are not responsible for what property managers do with information after a Doora Card
            has been submitted. Property managers have their own legal obligations under the Privacy
            Act.
          </li>
          <li>
            We are not responsible for data held on WhatsApp, Instagram, or Facebook outside our
            control. When you message a Helper through those platforms, those platforms have their
            own data policies.
          </li>
        </ul>
        <p>
          Nothing in these terms limits our liability for fraud, personal injury caused by our
          negligence, or any other liability that cannot be lawfully excluded.
        </p>
        <p>
          If you are a consumer under the Australian Consumer Law, you may have statutory guarantees
          that apply to services we provide. Those guarantees cannot be excluded, restricted, or
          modified. To the extent they apply, our liability for a failure to comply with a consumer
          guarantee is limited (at our option) to re-supplying the service or paying the cost of
          re-supply. For a free service that cost you nothing, the practical effect of this is
          nominal.
        </p>
        <p>
          Indirect and consequential damages. To the maximum extent permitted by law, neither you
          nor DOORA is liable to the other for any indirect, consequential, special, incidental,
          exemplary, or punitive damages, including loss of profit, loss of opportunity, loss of
          data, or loss of business, arising in connection with these Terms. This exclusion does not
          affect the consumer guarantees under the Australian Consumer Law.
        </p>
        <p>
          Aggregate cap. Subject to the consumer guarantees under the Australian Consumer Law, and
          to any liability that cannot be lawfully limited (including for fraud or personal injury
          caused by our negligence), DOORA’s total aggregate liability to you for all claims arising
          in connection with these Terms is limited to AUD $100.
        </p>
        <h4>A8 Beta Product Notice</h4>
        <p>
          DOORA is currently in beta. This means we are still building and improving the platform.
          Some features may change, have limitations, or may not perform as intended at all times.
        </p>
        <p>By using DOORA during the beta period, you acknowledge:</p>
        <ul>
          <li>The platform is under active development and may contain bugs.</li>
          <li>DOORA will use reasonable efforts to maintain platform availability during beta.</li>
          <li>Features may be added, changed, or removed without notice.</li>
          <li>We will notify you of any material changes to these Terms.</li>
        </ul>
        <p>
          Our commitment to protecting your personal information does not change during the beta
          period. Deletion-by-default applies from day one.
        </p>
        <h4>A9 Changes, Suspension, and Service Availability</h4>
        <h5>Changes to these Terms</h5>
        <p>
          DOORA may update these Terms by giving you 30 days’ notice. If you do not agree, you may
          delete your account before the change takes effect. Your continued use after the change
          takes effect means you accept the updated Terms. For changes to data practices or privacy
          terms, we will also give you a prominent in-platform notification.
        </p>
        <h5>You can leave at any time</h5>
        <p>
          You can stop using DOORA at any time. Delete your account and request deletion of your
          data through the platform, or message a Helper to do so.
        </p>
        <h5>Our right to suspend or remove access</h5>
        <p>DOORA may suspend or remove your access in the following situations:</p>
        <ul>
          <li>
            Immediate suspension (no prior notice): If you submit fraudulent information, engage in
            conduct that is abusive, harassing, or threatening towards Helpers, property managers, or
            other users, or breach Australian law. We will tell you why within 48 hours.
          </li>
          <li>
            Suspension with 7 days’ notice: For other breaches of these Terms (for example,
            submitting misleading information). You will receive written notice describing the issue
            and 7 days to respond or fix it before suspension takes effect.
          </li>
          <li>
            Account closure with 30 days’ notice: DOORA may close your account for any reason by
            giving you 30 days’ written notice. During those 30 days, you can export or delete your
            data.
          </li>
        </ul>
        <h5>Things outside our control</h5>
        <p>
          DOORA may be unavailable from time to time for reasons outside our control, including
          outages at our hosting provider, failures at third-party services we rely on, or other
          technical events we cannot prevent. We are not liable for service unavailability caused by
          events beyond our reasonable control. We will restore the service as quickly as we can and
          communicate with you about it where possible.
        </p>
        <h4>A9A Platform Data and Intellectual Property</h4>
        <h5>Your personal information</h5>
        <p>
          Your personal information remains your own. DOORA collects it solely to provide the DOORA
          service to you and for no other purpose. The commitments in Section 8 of the DOORA Privacy
          Policy (What We Will Never Do) are absolute and cannot be overridden by this section or
          any other term.
        </p>
        <h5>Platform intellectual property</h5>
        <p>
          The DOORA platform, including its source code, design system, algorithms, data models,
          brand assets, and all associated intellectual property, is owned exclusively by RL Group
          Australia Pty Ltd. Nothing in these Terms grants you any licence, right, or interest in
          the platform’s intellectual property beyond the limited right to use the DOORA service for
          the purpose of preparing and submitting rental applications.
        </p>
        <h4>A10 Governing Law</h4>
        <p>
          These Terms are governed by the laws of New South Wales, Australia. Each party submits to
          the non-exclusive jurisdiction of the courts of New South Wales.
        </p>
        <p>Nothing in these Terms limits any rights you have under the Australian Consumer Law.</p>
        <h5>A10A Force Majeure</h5>
        <p>
          DOORA is not liable for any failure to perform its obligations if caused by events beyond
          its reasonable control, including: infrastructure or hosting outages, third-party service
          interruptions, regulatory changes, government actions, pandemics, natural disasters, or
          events of war or terrorism. In such cases, DOORA will restore the service as quickly as
          reasonably possible and communicate with you where possible.
        </p>
        <h5>A10B Dispute Resolution</h5>
        <p>
          If you have a complaint or dispute about DOORA or the service, you may contact us at
          support@doora.com.au. We will acknowledge your complaint within 5 business days and use
          reasonable endeavours to resolve it within 30 days.
        </p>
        <p>
          If we cannot resolve your complaint to your satisfaction, you may: (a) lodge a complaint
          with the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au if
          your complaint relates to how we handle your personal information; or (b) commence
          proceedings in the courts of New South Wales.
        </p>
        <h4>A11 Assignment and Acquisition</h4>
        <p>
          DOORA may assign or transfer these Terms to a successor entity (for example, in an
          acquisition or merger). If this happens:
        </p>
        <ul>
          <li>We will give you at least 30 days’ notice before the transfer takes effect.</li>
          <li>
            You will have the right to delete your account and all your data before the transfer is
            completed.
          </li>
          <li>
            If you do not delete your account within the notice period, these Terms (including the
            DOORA Privacy Policy) will continue to apply under the new entity.
          </li>
        </ul>
        <p>
          DOORA will provide written notice of any transfer of your personal information to a third
          party. You will retain the right to delete your account before such transfer takes effect.
        </p>
        <h4>A12 General provisions</h4>
        <p>
          Severability. If any provision of these Terms is held invalid or unenforceable, the
          remaining provisions remain in full force and effect, and the invalid provision will be
          modified to the minimum extent necessary to make it valid.
        </p>
        <p>
          Entire agreement. These Terms, together with the DOORA Privacy Policy, are the entire
          agreement between you and DOORA in relation to the DOORA service and supersede all prior
          representations, communications, and agreements.
        </p>
        <p>
          Notices. DOORA may give you notice through the platform, by SMS, or by email to the
          address you have on file. You may give DOORA notice by emailing support@doora.com.au.
        </p>
        <p>
          No waiver. A failure or delay by DOORA in enforcing any provision of these Terms is not a
          waiver of that provision.
        </p>
        <p></p>
        <p></p>
        <p></p>
        <p></p>
          <button
            type='button'
            className='btn fw-bold py-2 px-3 rounded-pill btn-doora'
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </ApplicationResponsiveModal>
  )
}
